import { createQueryKeys } from '@lukemorales/query-key-factory'

import type { UseQueryOptionsWithoutQueryFn } from '~/types/util/tanstack-query'

import { type IncludableField, useFetchViews } from './useFetchViews'

export const queryKeys = createQueryKeys('views', {
  all: ({
    workspaceId,
    fieldsToInclude = [],
    params,
  }: {
    workspaceId: MaybeRef<string>
    fieldsToInclude?: IncludableField[]
    params?: MaybeRef<Record<string, any>>
  }) => ({
    queryKey: [workspaceId, ...(fieldsToInclude.length ? [[...fieldsToInclude].sort()] : []), ...(params ? [params] : [])],
  }),
  byId: ({
    id,
    fieldsToInclude = [],
    workspaceId,
  }: {
    id: MaybeRef<string>
    fieldsToInclude?: IncludableField[]
    workspaceId: MaybeRef<string>
  }) => ({
    queryKey: [workspaceId, id, ...(fieldsToInclude.length ? [[...fieldsToInclude].sort()] : [])],
  }),
})

const all = ({
  fieldsToInclude = [],
  params,
  useQueryOptions = {},
}: {
  fieldsToInclude?: IncludableField[]
  params?: MaybeRef<Record<string, any>>
  useQueryOptions?: UseQueryOptionsWithoutQueryFn
} = {}) => {
  const { getAll } = useFetchViews()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.all({
      workspaceId: activeWorkspaceId as MaybeRef<string>,
      fieldsToInclude,
      params,
    }),
    queryFn: () => getAll({
      params: unref(params),
      workspaceId: unref(activeWorkspaceId) ?? '',
      fieldsToInclude: unref(fieldsToInclude),
    }),
    enabled: () => !!unref(activeWorkspaceId),
    ...useQueryOptions,
  })
}

export type QueryViewByIdCacheData = Awaited<ReturnType<ReturnType<typeof useFetchViews>['getById']>>
const byId = ({
  id,
  fieldsToInclude = [],
  useQueryOptions = {},
}: {
  id: MaybeRef<string>
  fieldsToInclude?: IncludableField[]
  useQueryOptions?: UseQueryOptionsWithoutQueryFn
}) => {
  const { getById } = useFetchViews()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.byId({
      id,
      fieldsToInclude,
      workspaceId: activeWorkspaceId as MaybeRef<string>,
    }),
    queryFn: () => getById({
      id: unref(id),
      fieldsToInclude: unref(fieldsToInclude),
      workspaceId: unref(activeWorkspaceId) ?? '',
    }),
    enabled: () => !!unref(activeWorkspaceId),
    ...useQueryOptions,
  })
}

export const useQueryViews = {
  all,
  byId,
}
